import './ImportPromptsDialog.scss';
import React, { useRef, useState } from 'react';
import SimpleBar from 'simplebar-react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { importPrompts } from '../../services/monitor/prompt/ImportPromptsService';

const CSV_PROMPT_LIMIT = 300;

interface ImportPromptsDialogProps {
  visible: boolean;
  onHide: () => void;
  projectId: number;
  availableSlots: number;
  onSuccess: () => void;
}

const ImportPromptsDialog: React.FC<ImportPromptsDialogProps> = ({
  visible,
  onHide,
  projectId,
  availableSlots,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [parsedPrompts, setParsedPrompts] = useState<string[]>([]);
  const [selectedPrompts, setSelectedPrompts] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [importedCount, setImportedCount] = useState<number | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const isOverLimit = parsedPrompts.length > availableSlots;

  const { mutate: runImport, isPending } = useMutation({
    mutationFn: ({ projectId, prompts }: { projectId: number; prompts: string[] }) =>
      importPrompts(projectId, { prompts }),
    onSuccess: (_, variables) => {
      setImportedCount(variables.prompts.length);
      onSuccess();
      handleClose();
    },
    onError: () => {
      setError(t('csvImportErrorInvalidFormat'));
    },
  });

  const processFile = (file: File) => {
    setError(null);
    setImportedCount(null);
    setFileName(file.name);

    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target?.result as string;
      const lines = text.split('\n').map((l) => l.trim()).filter(Boolean);
      const dataLines = lines[0]?.toLowerCase().includes('prompt') ? lines.slice(1) : lines;
      const firstDataLine = dataLines[0] ?? '';
      const delimiter = firstDataLine.includes(';') && !firstDataLine.includes(',') ? ';' : ',';
      const prompts = dataLines
        .map((line) => {
          const col = line.split(delimiter)[0].replace(/^"|"$/g, '').trim();
          return col;
        })
        .filter(Boolean);

      if (prompts.length === 0) {
        setError(t('csvImportErrorInvalidFormat'));
        setParsedPrompts([]);
        setSelectedPrompts([]);
        return;
      }

      if (prompts.length > CSV_PROMPT_LIMIT) {
        setError(t('csvImportErrorLimitExceeded'));
        setParsedPrompts([]);
        setSelectedPrompts([]);
        return;
      }

      setParsedPrompts(prompts);
      setSelectedPrompts(prompts.slice(0, availableSlots));
    };
    reader.readAsText(file);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    processFile(file);
  };

  const handleDragEnter = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragOver = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.csv')) {
      setError(t('csvImportErrorInvalidFormat'));
      return;
    }
    processFile(file);
  };

  const togglePrompt = (prompt: string) => {
    setSelectedPrompts((prev) => {
      if (prev.includes(prompt)) {
        return prev.filter((p) => p !== prompt);
      }
      if (prev.length >= availableSlots) return prev;
      return [...prev, prompt];
    });
  };

  const handleImport = () => {
    if (selectedPrompts.length === 0) return;
    runImport({ projectId, prompts: selectedPrompts });
  };

  const handleClose = () => {
    setParsedPrompts([]);
    setSelectedPrompts([]);
    setError(null);
    setFileName(null);
    setImportedCount(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    onHide();
  };

  return (
    <Dialog
      visible={visible}
      onHide={handleClose}
      header={t('importFromCsv')}
      className="dialog-box import-prompts-dialog"
      style={{ width: '500px' }}
    >
      <div className="import-prompts-dialog__content">
        <p className="import-prompts-dialog__desc">
          {t('csvImportDialogDesc')}
        </p>

        <ul className="import-prompts-dialog__format-list">
          <li>{t('csvImportFormatOnePerRow')}</li>
          <li>{t('csvImportFormatFirstColumn')}</li>
          <li>{t('csvImportFormatPlainText')}</li>
        </ul>

        <a
          href="/prompt-template.csv"
          download="prompt-template.csv"
          className="import-prompts-dialog__template-link"
        >
          <span className="ico--download ico--xs" />
          {t('csvDownloadTemplate')}
        </a>

        {/* Hidden real input */}
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv"
          onChange={handleFileChange}
          className="import-prompts-dialog__file-input--hidden"
          id="import-csv-input"
        />
        {/* Custom styled trigger */}
        <label
          htmlFor="import-csv-input"
          className={`import-prompts-dialog__upload-zone${isDragging ? ' import-prompts-dialog__upload-zone--dragging' : ''}`}
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <span className="ico--download ico--lg import-prompts-dialog__upload-icon" />
          <span className="import-prompts-dialog__upload-text">
            {isPending
              ? t('csvImportUploading')
              : fileName
              ? fileName
              : t('csvImportClickToSelect')}
          </span>
        </label>

        {error && <div className="import-prompts-dialog__error">{error}</div>}

        {importedCount !== null && !error && (
          <div className="import-prompts-dialog__success">
            {t('csvImportSuccess', { count: importedCount })}
          </div>
        )}

        {parsedPrompts.length > 0 && (
          <>
            {isOverLimit && (
              <div className="import-prompts-dialog__limit-warning">
                {t('csvImportSlotWarning', { slots: availableSlots })}
                {' '}
                {t('csvImportSelected', { selected: selectedPrompts.length, slots: availableSlots })}
              </div>
            )}

            <SimpleBar style={{ maxHeight: '280px' }}>
              <ul className="import-prompts-dialog__list">
              {parsedPrompts.map((prompt, i) => {
                const isSelected = selectedPrompts.includes(prompt);
                const isDisabled = isOverLimit && !isSelected && selectedPrompts.length >= availableSlots;
                return (
                  <li
                    key={i}
                    className={`import-prompts-dialog__item${isDisabled ? ' import-prompts-dialog__item--disabled' : ''}`}
                  >
                    {isOverLimit ? (
                      <Checkbox
                        checked={isSelected}
                        disabled={isDisabled}
                        onChange={() => togglePrompt(prompt)}
                      />
                    ) : (
                      <span className="ico--tick ico--xs import-prompts-dialog__check" />
                    )}
                    <span className="import-prompts-dialog__prompt">{prompt}</span>
                  </li>
                );
              })}
              </ul>
            </SimpleBar>

            <div className="import-prompts-dialog__footer">
              <Button
                className="btn btn--sm"
                label={t('csvImportButton', { count: selectedPrompts.length })}
                onClick={handleImport}
                disabled={selectedPrompts.length === 0 || isPending}
              />
              <Button className="btn btn--sm btn--secondary" label={t('cancel')} onClick={handleClose} />
            </div>
          </>
        )}
      </div>
    </Dialog>
  );
};

export default ImportPromptsDialog;
